import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import Breadcrumbs from '../Components/Breadcrumbs';
import Table from '../Components/Tables';
import Button from '../Components/Buttons';
import { AgentApiProvider, useAgentApi } from '../Services/AgentApiProvider';
import { ConfirmationProvider } from '../Components/ConfirmationContext';
import { AlertProvider, useAlert } from '../Components/AlertContext';
import { useConfirmation } from '../Components/ConfirmationContext';
import { formatDateUTC } from '../utils/formatDateUTC';
import {useAuth} from "../Contexts/AuthContext";

function AgentsHub({canEdit}) {
    const { organizationId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const { showAlert } = useAlert();
    const { showConfirmation } = useConfirmation();
    const { status } = useAuth();
    const isDocumentPermission = status?.permissions?.includes('all:document');
    const {
        getAgents,
        deleteAgent,
        getAgentNotifications,
        getAgentCatalog,
        getAgentFrames,
        getAgentMarkers,
        createAgent,
        getAgentSwarm,
    } = useAgentApi();
    
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [alertShown, setAlertShown] = useState(false);
    const [agents, setAgents] = useState([]);
    const [totalAgents, setTotalAgents] = useState(0);
    const [itemsPerPage, setItemsPerPage] = useState(10);

    const [selectedRows, setSelectedRows] = useState([]);
    const selectedId = selectedRows.length === 1 ? selectedRows[0].key : null;

    useEffect(() => {
        if (location.state?.successMessage && !alertShown) {
            showAlert('success', location.state.successMessage);
            setAlertShown(true);
            navigate(location.pathname, { replace: true, state: {} });
        }
        if (location.state?.returnMessage && !alertShown) {
            showAlert('help', location.state.returnMessage);
            setAlertShown(true);
            navigate(location.pathname, { replace: true, state: {} });
          }
    }, [location.state?.successMessage, alertShown, navigate, location.pathname, showAlert]);

    const assistantTypes = {
        "SIMPLE": "Simple",
        "RAG": "RAG"
    }

    const columns = [ 
        { name: 'ID', selector: (row) => row.key, sortable: false },
        { name: 'Name', selector: (row) => row.name, sortable: false },
        { name: 'Type', selector: (row) => assistantTypes[row.assistant_type], sortable: false },
        { name: 'Status', selector: (row) => row.is_active === true ? "Active" : "InActive", sortable: false },
        { name: 'Created', selector: (row) => formatDateUTC(row.created_at), sortable: false },
        { name: 'Updated', selector: (row) => formatDateUTC(row.updated_at), sortable: false },
    ];

    const handleRowClicked = (row) => {
        navigate(`/organizations/${organizationId}/agents-hub/${row.key}`, { state: { organizationId } });
    };

    const handleDelete = async (selectedId) => {
        if (!selectedId) return;

        showConfirmation(
            `Are you sure you want to delete the selected item?`,
            async () => {
                try {
                    setLoading(true);
                    await deleteAgent(selectedId);
                    await fetchAgents(0, itemsPerPage);
                    showAlert('success', 'Item was successfully deleted!');
                } catch (error) {
                    showAlert('fail', 'Failed to delete the item!');
                } finally {
                    setLoading(false);
                }
            }
        );
    };

    const createCopy = async () => {
        if (!selectedId) return;
        setLoading(true);

        try {
            const agentToCopy = agents.find(agent => agent.key === selectedId);
            if (!agentToCopy) {
                showAlert('fail', 'Agent not found');
                setLoading(false);
                return;
            }

            const escapeRegExp = (string) =>
                string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

            const baseName = agentToCopy.name;
            const safeBaseName = escapeRegExp(baseName);
            const copyNumbers = agents
                .map(a => {
                    const match = a.name.match(new RegExp(`^${safeBaseName} \\(Copy (\\d+)\\)$`));
                    return match ? parseInt(match[1], 10) : null;
                })
                .filter(n => n !== null);
            const nextCopyNumber = copyNumbers.length > 0 ? Math.max(...copyNumbers) + 1 : 1;

            const [notifications, frames, markers, swarms, catalogData] = await Promise.all([
                getAgentNotifications(selectedId).then(n => n?.map(a => a.id) ?? []),
                getAgentFrames(selectedId).then(f => f?.map(a => a.id) ?? []),
                getAgentMarkers(selectedId).then(m => m?.map(a => a.id) ?? []),
                getAgentSwarm(selectedId).then(s => s?.map(s => s.key) ?? []),
                isDocumentPermission ? getAgentCatalog(selectedId).then(c => c?.id ?? null) : Promise.resolve(null),
            ]);

            const newAgentData = {
                name: `${baseName} (Copy ${nextCopyNumber})`,
                prompt_id: agentToCopy.prompt_id,
                assistant_type: agentToCopy.assistant_type,
                temperature: agentToCopy.temperature,
                details_level: agentToCopy.details_level,
                model_name: agentToCopy.model_name,
                reasoning: agentToCopy.reasoning ?? "minimal",
                memory_type: agentToCopy.memory_type,
                memory_limit: agentToCopy.memory_limit,
                document_collection_keys: agentToCopy.document_collection_keys,
                connections: agentToCopy.connections,
                taxonomy_dictionary_id: agentToCopy.taxonomy_dictionary_id,
                skills: agentToCopy.skills,
                templates: agentToCopy.templates,
                is_active: agentToCopy.is_active,
                frame_keys: frames,
                marker_keys: markers,
                swarm: swarms,
                notification_keys: notifications,
                catalog_key: catalogData,
            };

            await createAgent(newAgentData);

            await fetchAgents(0, itemsPerPage);

            showAlert('success', 'Agent copy was successfully created!');
        } catch (error) {
            console.error("Create copy error:", error);
            showAlert('fail', 'Failed to create a copy of the agent!');
        } finally {
            setLoading(false);
        }
    };


    const fetchAgents = async (skip, limit) => {
        if (limit !== itemsPerPage) {
            setItemsPerPage(limit);
        }

        setLoading(true);
        setError(null);
        
        try {
            const response = await getAgents(skip, limit);
            setAgents(response.items);
            setTotalAgents(response.totalCount);
        } catch (error) {
            setError(error.message);
            showAlert('fail', 'Failed to fetch agents!');
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        fetchAgents(0, itemsPerPage);
    }, []);

    return (
        <div className="container">
            <Breadcrumbs />
            <div className="row d-flex gap">
                <div className="radius-yes p-2 bg-yes col-12">
                    {error ? (
                        <div>Error: {error}</div>
                    ) : (
                        <>
                            <Table
                                title="Agents"
                                columns={columns}
                                data={agents}
                                selectableRows
                                selectableRowsHighlight
                                paginationPerPage={itemsPerPage}
                                progressPending={loading}
                                paginationTotalRows={totalAgents}
                                onRowClicked={handleRowClicked}
                                sortIcon={<i className="fa-solid fa-chevron-down"></i>}
                                onDelete={handleDelete}
                                onChangePage={fetchAgents}
                                getRowUrl={(row) => `/organizations/${organizationId}/agents-hub/${row.key}`}
                                onSelectedRowsChange={({ selectedRows }) => {
                                    setSelectedRows(selectedRows);
                                }}

                            />
                            {canEdit &&(
                                <Button
                                    background="color2"
                                    size="large"
                                    onClick={() => navigate(`/organizations/${organizationId}/agents-hub/new-agent`)}
                                >
                                    Create New Agent
                                </Button>
                            )}
                            {selectedId && (
                                <Button variant="ghost" size="medium" onClick={createCopy}>
                                    Create copy
                                </Button>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}

function AgentsHubWrapper({canEdit}) {
    return (
        <AlertProvider>
            <ConfirmationProvider>
                <AgentApiProvider>
                    <AgentsHub canEdit={canEdit}/>
                </AgentApiProvider>
            </ConfirmationProvider>
        </AlertProvider>
    );
}

export default AgentsHubWrapper;